// components/minimal/MinimalPageContainer.tsx
//
// Flat-dark page shell. Same prop API as the classic PageContainer
// (so the runtime switch in ThemedPageContainer can hand it the
// same children + title without per-page rewrites), but a much
// quieter visual language:
//
//   - Solid #0E1118 background (no radial gradient).
//   - Plain white page title in Rajdhani — no gradient text, no
//     entrance keyframes. Cyan accent is reserved for the primary
//     action / subtitle dot.
//   - Container with a tighter max width (lg) and looser inner
//     padding for breathing room.
//   - MotionPage wraps the children so every route change fades +
//     slides in. Exit animations need AnimatePresence one level up
//     at the router; this component carries the enter side.
//
// NavMenu rendering is left to ThemedPageContainer so the classic
// nav and minimal nav can coexist behind the same flag.

import { ReactNode, ReactElement, ElementType } from 'react';
import { Box, Typography, Container, BoxProps, SvgIconProps } from '@mui/material';
import { SxProps, Theme, Breakpoint } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { MotionPage } from './MotionPage';
import type { UserData } from '../../context/AuthContext';

export interface MinimalPageContainerProps extends Omit<BoxProps, 'title' | 'maxWidth'> {
  children: ReactNode;
  user?: UserData | null;
  title?: string;
  subtitle?: string;
  /** Kept in the prop API for classic-compat — ignored in minimal
   *  mode where the title is always solid white. */
  titleGradient?: boolean;
  maxWidth?: Breakpoint | false;
  centered?: boolean;
  action?: ReactNode;
  sx?: SxProps<Theme>;
  /** Optional slot for the minimal-mode nav. The wiring lands in
   *  ThemedPageContainer so this component stays nav-agnostic. */
  nav?: ReactNode;
}

const PAGE_BG = '#0E1118';
const TEXT_PRIMARY = '#F5F7FA';
const TEXT_MUTED = '#8892A0';
const ACCENT = '#00FFD4';

export function MinimalPageContainer({
  children,
  title,
  subtitle,
  maxWidth = 'lg',
  centered = false,
  action,
  sx = {},
  nav,
  ...props
}: MinimalPageContainerProps): ReactElement {
  const location = useLocation();

  return (
    <Box
      sx={{
        backgroundColor: PAGE_BG,
        minHeight: '100vh',
        color: TEXT_PRIMARY,
        ...sx,
      }}
      {...props}
    >
      {nav}

      <Container
        maxWidth={maxWidth}
        sx={{
          pt: nav ? '88px' : 4,
          pb: { xs: 12, sm: 6 },
          px: { xs: 2, sm: 4 },
        }}
      >
        <MotionPage pageKey={location.pathname}>
          {title && (
            <Box
              sx={{
                mb: 5,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: centered ? 'center' : 'flex-start',
                justifyContent: centered ? 'center' : 'space-between',
                textAlign: centered ? 'center' : 'left',
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontFamily: '"Rajdhani", sans-serif',
                    fontWeight: 600,
                    color: TEXT_PRIMARY,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: TEXT_MUTED,
                      mt: 0.5,
                      // Single accent dot at the start to draw a
                      // line back to the brand colour without
                      // splashing cyan across the heading.
                      '&::before': {
                        content: '"•"',
                        color: ACCENT,
                        mr: 1,
                      },
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {action && <Box>{action}</Box>}
            </Box>
          )}

          <Box>{children}</Box>
        </MotionPage>
      </Container>
    </Box>
  );
}

export interface MinimalEmptyStateProps extends BoxProps {
  icon?: ElementType<SvgIconProps>;
  title?: string;
  description?: string;
  action?: ReactNode;
  sx?: SxProps<Theme>;
}

export function MinimalEmptyState({
  icon: Icon,
  title = 'No data',
  description,
  action,
  sx = {},
  ...props
}: MinimalEmptyStateProps): ReactElement {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 10,
        px: 2,
        ...sx,
      }}
      {...props}
    >
      {Icon && (
        <Icon
          sx={{
            fontSize: 56,
            color: '#3A4452',
            mb: 2,
          }}
        />
      )}
      <Typography
        variant="h6"
        sx={{
          fontFamily: '"Rajdhani", sans-serif',
          color: TEXT_MUTED,
          fontWeight: 500,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: '#4A5568', mb: 3 }}>
          {description}
        </Typography>
      )}
      {action}
    </Box>
  );
}

export default MinimalPageContainer;
